import React from 'react'
import { FaEdit, FaSave, FaTimes, FaTimesCircle, FaTrashAlt } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import { roundOffPrecicion } from '../../utils/quantityConversion';

const StatementTable = ({
    company,
    selectedOutlet,
    filteredData,
    searchTerm,
    data,
    editingId,
    editName,
    setEditingId,
    setEditName,
    updateConfirmation,
    handleEditChange,
    handleSaveEdit,
    handleCancelEdit,
    handleEditItem,
    handleDeleteItem,
    formatSalesValue,
}) => {
    const formatUnitDefinitions = (item) => {
        const defs = item.unit_definitions;
        if (!defs || defs.length === 0) return null;

        return defs.map((def, i) => (
            <span key={i}>
                {def.unit} ({def.uom})
            </span>
        ));
    };

    const hasEditingRow = filteredData.some(group =>
        group.stocks.some(item => item.isEditing)
    );

    return (
        <>
            <div className="text-center py-2">
                <div className="text-xl font-bold text-[#2C2C2A] tracking-wide">
                    {company}
                </div>
                <div className="text-base font-medium text-[#5A5854] tracking-wide">
                    {decodeURIComponent(selectedOutlet || "")}
                </div>
            </div>

            <div className="overflow-auto tiny-scrollbar w-full">
                <table className="w-full text-sm text-center border-collapse rounded-lg overflow-hidden shadow-lg border-[1px] border-[#F0EDE8]">
                    <thead>
                        <tr className="border-b border-[#F0EDE8] bg-white border-t">
                            {[
                                "Item Name",
                                "Brand",
                                "Level",
                                "Primary Unit",
                                "Secondary Unit",
                                "Rate",
                                "Total",
                                ...(hasEditingRow ? [] : ["Unit Definition"]),
                                "Actions",
                            ].map((header) => (
                                <th
                                    key={header}
                                    className="px-1.5 py-2.5 text-xs font-semibold uppercase tracking-widest text-[#8A8780] whitespace-nowrap"
                                >
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {filteredData.map((group, id) => (
                            <React.Fragment key={id}>
                                <tr className="bg-[#FAFAF8]">
                                    <td
                                        className="px-4 py-2 text-left font-semibold capitalize text-[#4B4B46] text-sm tracking-wider"
                                        colSpan={hasEditingRow ? 8 : 9}
                                    >
                                        {group.group}
                                    </td>
                                </tr>

                                {group.stocks.map((item) => {
                                    const uniqueId = `${item.ItemName}-${item.GroupName}`;

                                    return (
                                        <tr
                                            key={uniqueId}
                                            className="border-b border-[#F8F7F3] hover:bg-[#FAFAF8] transition-colors duration-100"
                                        >
                                            {/* Item Name */}
                                            <td className="px-1.5 py-2 whitespace-nowrap font-medium text-[#2C2C2A]">
                                                <div className="flex items-center justify-center gap-2">
                                                    {editingId === uniqueId ? (
                                                        <>
                                                            <input
                                                                value={editName}
                                                                onChange={(e) => setEditName(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        updateConfirmation(item.ItemName, editName);
                                                                    }
                                                                    if (e.key === "Escape") {
                                                                        setEditingId(null);
                                                                    }
                                                                }}
                                                                className="border border-[#E5E2DB] px-2 py-1 rounded text-sm outline-none focus:ring-2 focus:ring-[#C7C3BD] transition"
                                                                autoFocus
                                                            />
                                                            <FaCheck
                                                                className="text-[#059669] cursor-pointer transition-all duration-200 hover:scale-110 active:scale-95"
                                                                onClick={() => updateConfirmation(item.ItemName, editName)}
                                                            />
                                                            <FaTimes
                                                                className="text-[#DC2626] cursor-pointer transition-all duration-200 hover:scale-110 active:scale-95"
                                                                onClick={() => setEditingId(null)}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span
                                                                className='hover:underline cursor-pointer'
                                                                onClick={() => window.open(`/items?item_name=${encodeURIComponent(item.ItemName)}`, "_blank")}
                                                            >
                                                                {item.ItemName}
                                                            </span>
                                                            <FaEdit
                                                                className="text-[#8A8780] cursor-pointer transition-all duration-200 hover:scale-110 hover:text-[#4B4B46] active:scale-95"
                                                                onClick={() => {
                                                                    setEditingId(uniqueId);
                                                                    setEditName(item.ItemName);
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Brand */}
                                            <td className="px-1.5 py-2 whitespace-nowrap font-medium text-[#5A5854]">
                                                {item.BrandName || "—"}
                                            </td>

                                            {/* Current Level */}
                                            <td className="px-1.5 py-2 font-semibold text-[#4B4B46]">
                                                {item.isEditing ? (
                                                    <input
                                                        type="number"
                                                        name="CurrentLevel"
                                                        value={item.editValues.CurrentLevel}
                                                        onChange={(e) => handleEditChange(uniqueId, e)}
                                                        min="0"
                                                        step="0.01"
                                                        className="w-24 text-center px-2 py-1 border border-[#E5E2DB] rounded"
                                                    />
                                                ) : (
                                                    roundOffPrecicion(item.CurrentLevel)
                                                )}
                                            </td>

                                            {/* Primary UOM */}
                                            <td className="px-1.5 py-2 whitespace-nowrap">
                                                {item.isEditing ? (
                                                    <input
                                                        type="text"
                                                        name="primary_unit"
                                                        value={item.editValues.primary_unit}
                                                        onChange={(e) => handleEditChange(uniqueId, e)}
                                                        className="w-24 text-center px-2 py-1 border border-[#E5E2DB] rounded"
                                                    />
                                                ) : (
                                                    <span >
                                                        {item.primary_unit || item.UOM || "—"}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Secondary Unit */}
                                            <td className="px-1.5 py-2 whitespace-nowrap">
                                                {item.isEditing ? (
                                                    <input
                                                        type="text"
                                                        name="secondary_unit"
                                                        value={item.editValues.secondary_unit}
                                                        onChange={(e) => handleEditChange(uniqueId, e)}
                                                        className="w-24 text-center px-2 py-1 border border-[#E5E2DB] rounded"
                                                    />
                                                ) : (
                                                    <span >
                                                        {item.secondary_unit || "—"}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Rate */}
                                            <td className="px-1.5 py-2 font-medium text-[#5A5854]">
                                                {item.isEditing ? (
                                                    <input
                                                        type="number"
                                                        name="Rate"
                                                        value={item.editValues.Rate}
                                                        onChange={(e) => handleEditChange(uniqueId, e)}
                                                        min="0"
                                                        step="0.01"
                                                        className="w-24 text-center px-2 py-1 border border-[#E5E2DB] rounded"
                                                    />
                                                ) : (
                                                    formatSalesValue(item.Rate)
                                                )}
                                            </td>

                                            {/* Total */}
                                            <td className="px-1.5 py-2 font-semibold text-[#2C2C2A]">
                                                {item.isEditing
                                                    ? formatSalesValue(item.editValues.Total)
                                                    : formatSalesValue(item.Total)}
                                            </td>

                                            {/* Unit Defintion */}
                                            {!hasEditingRow && (
                                                <td className="px-1.5 py-2 font-semibold text-[#2C2C2A]">
                                                    {formatUnitDefinitions(item)}
                                                </td>
                                            )}

                                            {/* Actions */}
                                            <td className="px-1.5 py-2 whitespace-nowrap">
                                                {item.isEditing ? (
                                                    <div className="flex justify-center space-x-2">
                                                        <button
                                                            onClick={() => handleSaveEdit(uniqueId)}
                                                            className="h-7 w-7 p-0 border border-[#E5E2DB] rounded-full flex items-center justify-center text-[#059669] hover:bg-green-50"
                                                        >
                                                            <FaSave className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleCancelEdit(uniqueId)}
                                                            className="h-7 w-7 p-0 border border-[#E5E2DB] rounded-full flex items-center justify-center text-[#DC2626] hover:bg-red-50"
                                                        >
                                                            <FaTimesCircle className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center space-x-2">
                                                        <button
                                                            onClick={() => handleEditItem(uniqueId)}
                                                            className="h-7 w-7 p-0 border border-[#E5E2DB] rounded-full flex items-center justify-center text-[#2563EB] hover:bg-blue-50"
                                                        >
                                                            <FaEdit className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteItem(uniqueId)}
                                                            className="h-7 w-7 p-0 border border-[#E5E2DB] rounded-full flex items-center justify-center text-[#DC2626] hover:bg-red-50"
                                                        >
                                                            <FaTrashAlt className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}

                                {!searchTerm && (
                                    <tr>
                                        <td colSpan={6} className="p-2 text-right pr-3 text-xs font-semibold text-[#64625c] uppercase tracking-wider border-t border-[#F0EDE8]">
                                            Group Total
                                        </td>
                                        <td className="p-2 text-center font-semibold text-[#2C2C2A] border-t border-[#F0EDE8]">
                                            {formatSalesValue(group.group_total)}
                                        </td>
                                        <td colSpan={hasEditingRow ? 1 : 2}></td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}

                        <tr>
                            <td colSpan={hasEditingRow ? 8 : 9} className="p-2"></td>
                        </tr>

                        {!searchTerm && (
                            <tr>
                                <td colSpan={6} className="p-2 text-right pr-3 text-xs font-bold text-[#4B4B46] uppercase tracking-wider border-t-2 border-[#E5E2DB]">
                                    Grand Total
                                </td>
                                <td className="p-2 text-center font-semibold text-[#2C2C2A] border-t-2 border-[#E5E2DB]">
                                    {formatSalesValue(data.reduce((acc, group) => acc + group.group_total, 0))}
                                </td>
                                <td colSpan={hasEditingRow ? 1 : 2} className="p-2 border-t-2 border-[#E5E2DB]"></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    )
}

export default StatementTable